// @ts-strict-ignore
import React, { useContext } from 'react';

import {
  Box,
  Text,
  TextField,
  Button,
  ButtonVariant,
  Modal,
  Select,
  Separator,
  IconName,
  Icon,
} from '@usealloy/component-library';

import { UserContext } from '~/dashboard/context';
import { StyledServiceCredentialsBanner } from '../../workflows/editor/components/NodeEditorDrawer/ServiceTabs/ServiceOptionsTab/Sections/styles';

const workflowActionOptionText = 'Changing credentials here will only affect the service in this workflow.';
const multiEnvironmentConfigText = 'Adding credentials here will only affect this workflow\'s test configuration.';

const NullOption = {
  name: 'Please select an owner',
  value: '',
};
const AlloyOption = {
  name: 'Alloy',
  value: 'alloy',
};
const CustomerOption = {
  name: 'Customer',
  value: 'customer',
};

export enum ServiceCredentialModalMode {
  SETTINGS = 'settings',
  WORKFLOW_ACTION_OPTIONS = 'workflow-action-options',
  MULTI_ENVIRONMENT_CONFIG = 'multi-environment-config',
}

type ServiceCredentialsTypes = {
  mode: ServiceCredentialModalMode;
  serviceName: string;
  serviceCredentials: {
    [key: string]: string;
  };
  credentialFormat: {
    ids: string[];
    secrets: string[];
    name_field: string;
  };
  credentialOwner: string;
  hasUpdatePermission: boolean;
  isOpen: boolean;
  handleServiceCredentialsClose: () => void;
  handleServiceCredentialsSave: () => void;
  handleServiceCredentialsChange: (event) => void;
  handleCredentialsOwnerChange: (event) => void;
  credentialsDescription?: string;
  handleCredentialsDescriptionChange?: (event) => void;
};

const ServiceCredentials = ({
  mode = ServiceCredentialModalMode.SETTINGS,
  serviceName,
  serviceCredentials,
  credentialFormat,
  credentialOwner,
  hasUpdatePermission,
  isOpen,
  handleServiceCredentialsClose,
  handleServiceCredentialsSave,
  handleServiceCredentialsChange,
  handleCredentialsOwnerChange,
  credentialsDescription,
  handleCredentialsDescriptionChange,
}: ServiceCredentialsTypes) => {
  const agent = useContext(UserContext)?.agent;

  const isAlloyCredential = credentialOwner === 'alloy';
  const isUnknownCredential = !credentialOwner || credentialOwner === 'null';
  const isCustomerCredential = credentialOwner === 'customer';
  const idOrSecretsFields = credentialFormat?.ids.concat(credentialFormat?.secrets);
  const isConfigurable = !!serviceCredentials && Object.keys(serviceCredentials).length > 0;
  const isCredentialFieldsAllEmpty = idOrSecretsFields?.every(field => serviceCredentials[field] === '');
  const isIdFieldsAllEmpty = credentialFormat?.ids?.every(field => serviceCredentials[field] === '');
  const isIdFieldsHasEmpty = credentialFormat?.ids?.some(field => serviceCredentials[field] === '');
  const closeButton = <Button onClick={handleServiceCredentialsClose}>Close</Button>;

  const saveButtonDisabled = mode === ServiceCredentialModalMode.MULTI_ENVIRONMENT_CONFIG
  // Disable save if there is not a value for everything
    ? (isUnknownCredential || isIdFieldsHasEmpty)
  // Disable save if:
  //  - Owner is picked, but there are some empty fields
  //  - Owner is not picked, but some fields are filled out
    : (isIdFieldsHasEmpty && !isUnknownCredential) || (isUnknownCredential && !isIdFieldsAllEmpty);

  const saveButton = (isConfigurable && hasUpdatePermission) ? (
    <Button
      variant={ButtonVariant.primary}
      disabled={saveButtonDisabled}
      onClick={handleServiceCredentialsSave}
    >
      {mode === ServiceCredentialModalMode.WORKFLOW_ACTION_OPTIONS
        ? 'Apply'
        : 'Save'}
    </Button>
  ) : '';

  const serviceCredentialSettings = {};
  const serviceCredentialSecrets = {};
  if (serviceCredentials) {
    Object.keys(serviceCredentials).forEach(key => {
      if (idOrSecretsFields?.includes(key)) {
        serviceCredentialSecrets[key] = serviceCredentials[key];
      } else {
        serviceCredentialSettings[key] = serviceCredentials[key];
      }
    });
  }

  const getCredentialOwnerOptions = () => {
    if (agent?.isEmployee) {
      return [NullOption, AlloyOption, CustomerOption];
    }
    if (credentialOwner === 'alloy') {
      return [NullOption, AlloyOption, CustomerOption];
    }
    return [NullOption, CustomerOption];
  };

  const isSecretField = (fieldName: string) => !!credentialFormat?.secrets.includes(fieldName);
  const isIdField = (fieldName: string) => !!credentialFormat?.ids.includes(fieldName);

  let infoText: string;
  switch (mode) {
    case ServiceCredentialModalMode.WORKFLOW_ACTION_OPTIONS:
      infoText = workflowActionOptionText;
      break;
    case ServiceCredentialModalMode.MULTI_ENVIRONMENT_CONFIG:
      infoText = multiEnvironmentConfigText;
      break;
    default:
      infoText = null;
  }

  return (
    <Modal
      header={serviceName}
      primaryAction={saveButton || closeButton}
      secondaryAction={saveButton ? closeButton : ''}
      size={isConfigurable ? 'large' : 'small'}
      onClose={handleServiceCredentialsClose}
      isOpen={isOpen}
    >
      {isConfigurable ? (
        <Box
          alignItems="flex-start"
          direction="column"
          gap={5}
          justifyContent="flex-start"
          style={{ marginTop: '-24px' }}
        >
          {infoText && (
            <StyledServiceCredentialsBanner>
              <Icon icon={IconName.Info} size="large" />
              <Text>
                {`${infoText} To edit environment-wide credentials, go to`}
                <a
                  href="/v3/dashboard/settings/services"
                  target="_blank"
                  style={{ textTransform: 'none', fontWeight: 300, color: 'var(--cl-border-focus-ring)' }}
                >
                  Service Settings.
                </a>
              </Text>
            </StyledServiceCredentialsBanner>
          )}

          {Object.keys(serviceCredentialSettings).length > 0 ? (
            <Box direction="column" gap={5} justifyContent="flex-start" alignItems="flex-start">
              <Text size={4} as="h2" weight={500}>Service Settings</Text>
              {serviceCredentialSettings
                ? (Object.keys(serviceCredentialSettings).map(credentialKey => (
                  <TextField
                    key={credentialKey}
                    type="text"
                    style={{ height: 36 }}
                    id={credentialKey}
                    label={credentialKey}
                    value={serviceCredentialSettings[credentialKey]}
                    onChange={handleServiceCredentialsChange}
                    // @ts-ignore
                    autoComplete="stopItChrome"
                  />
                ))) : ''}
              <Separator orientation="horizontal" style={{ width: '100%' }} />
            </Box>
          ) : ''}

          {Object.keys(serviceCredentialSecrets).length > 0 ? (
            <Box direction="column" gap={5} justifyContent="flex-start" alignItems="flex-start">
              <Text size={4} as="h2" weight={500}>Service Credentials</Text>
              <Select
                data-testid="credential_owner"
                label="Owner"
                name="owner"
                error={(isUnknownCredential && !isIdFieldsAllEmpty) ? 'This is required.' : undefined}
                onChange={handleCredentialsOwnerChange}
                options={getCredentialOwnerOptions()}
                defaultValue={credentialOwner}
              />
              {isAlloyCredential ? (
                <Text
                  style={{ color: '#8B8B82' }}
                >You are currently using Alloy credentials for this service.
                </Text>
              ) : ''}
              {(isCustomerCredential && !isCredentialFieldsAllEmpty) ? (
                <Text
                  style={{ color: '#8B8B82' }}
                >You will be paying for the usage of these credentials directly.
                </Text>
              ) : ''}
              {serviceCredentialSecrets
                ? (Object.keys(serviceCredentialSecrets).map(credentialKey => (
                  <TextField
                    key={credentialKey}
                    id={credentialKey}
                    label={credentialKey}
                    type="text"
                    // @ts-ignore
                    autoComplete="stopItChrome"
                    style={{ height: 36 }}
                    isSecret={isSecretField(credentialKey)}
                    error={isIdField(credentialKey) && !isUnknownCredential
                      && serviceCredentials[credentialKey] === '' ? 'This is required.' : undefined}
                    disabled={(isUnknownCredential
                      || (agent?.isAgent && isAlloyCredential)) && isIdField(credentialKey)}
                    value={(agent?.isAgent && !isCustomerCredential && isIdField(credentialKey)) ? '' : serviceCredentialSecrets[credentialKey]}
                    onChange={handleServiceCredentialsChange}
                    onBlur={handleServiceCredentialsChange}
                  />
                ))) : ' '}
            </Box>
          ) : ''}

          {mode === ServiceCredentialModalMode.MULTI_ENVIRONMENT_CONFIG && (
            <TextField
              id="credentialsDescription"
              label="Description"
              type="text"
              // @ts-ignore
              autoComplete="stopItChrome"
              style={{ height: 36 }}
              maxLength={25}
              value={credentialsDescription || ''}
              onChange={handleCredentialsDescriptionChange}
              onBlur={handleCredentialsDescriptionChange}
            />
          )}
        </Box>
      ) : (
        <Text>The credentials are not configurable.</Text>
      )}

    </Modal>
  );
};

export default ServiceCredentials;
